import type { Locale } from '@/i18n/config'
import { buildOgUrl } from '@/lib/og/url'
import { truncateDescription } from './text'

export const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'http://localhost:3000'

export function resolveOgImage(ogImage: string | undefined, title: string): string {
  return ogImage ?? buildOgUrl(title)
}

export function jsonLdScripts(
  jsonLd?: Record<string, unknown> | Record<string, unknown>[],
): { type: string; children: string }[] | undefined {
  const nodes = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []
  if (!nodes.length) return undefined
  const payload =
    nodes.length === 1
      ? { '@context': 'https://schema.org', ...nodes[0] }
      : { '@context': 'https://schema.org', '@graph': nodes }
  return [
    {
      type: 'application/ld+json',
      children: JSON.stringify(payload).replace(/</g, '\\u003c'),
    },
  ]
}

interface PageHeadInput {
  locale: Locale | string
  path: string
  title: string
  description: string
  ogImage?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  altPaths?: { fr?: string; en?: string }
}

export function pageHead({ locale, path, title, description, ogImage, jsonLd, altPaths }: PageHeadInput) {
  const url = `${SITE_URL}/${locale}${path}`
  const fullTitle = `${title} | CountAct`
  const metaDescription = truncateDescription(description)
  // Card headline uses the clean title (no "| CountAct" suffix — the card's
  // own logo carries the brand); og:image:alt below keeps the full title.
  const image = resolveOgImage(ogImage, title)
  const frPath = altPaths ? altPaths.fr : path
  const enPath = altPaths ? altPaths.en : path
  const alternateLinks = [
    ...(frPath !== undefined ? [{ rel: 'alternate', hrefLang: 'fr', href: `${SITE_URL}/fr${frPath}` }] : []),
    ...(enPath !== undefined ? [{ rel: 'alternate', hrefLang: 'en', href: `${SITE_URL}/en${enPath}` }] : []),
    ...(frPath !== undefined ? [{ rel: 'alternate', hrefLang: 'x-default', href: `${SITE_URL}/fr${frPath}` }] : []),
  ]
  return {
    meta: [
      { title: fullTitle },
      { name: 'description', content: metaDescription },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: image },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: fullTitle },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: image },
    ],
    links: [{ rel: 'canonical', href: url }, ...alternateLinks],
    scripts: jsonLdScripts(jsonLd),
  }
}
